import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  Users,
  Mail,
  Check,
  X,
  Clock,
  AlertCircle,
  User,
  Calendar,
  MessageSquare,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import communityService from "../../services/communityService";
import "./JoinRequestManagement.css";

const JoinRequestManagement = ({ communityId, community }) => {
  const [joinRequests, setJoinRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingRequests, setProcessingRequests] = useState(new Set());
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrev: false,
  });

  useEffect(() => {
    fetchJoinRequests();
  }, [communityId, pagination.currentPage]);

  const fetchJoinRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await communityService.getJoinRequests(
        communityId,
        pagination.currentPage,
        20
      );

      if (response.success) {
        setJoinRequests(response.data.joinRequests);
        setPagination(response.data.pagination);
      } else {
        throw new Error("Failed to fetch join requests");
      }
    } catch (error) {
setError(error.message || "Failed to fetch join requests");
      toast.error("Failed to load join requests");
    } finally {
      setLoading(false);
    }
  };

  const handleJoinRequest = async (userId, action, userInfo) => {
    try {
      setProcessingRequests((prev) => new Set([...prev, userId]));

      const response = await communityService.handleJoinRequest(
        communityId,
        userId,
        action
      );

      if (response.success) {
        // Remove the processed request from the list
        setJoinRequests((prev) =>
          prev.filter((req) => req.user._id !== userId)
        );

        // Update pagination total
        setPagination((prev) => ({
          ...prev,
          totalItems: Math.max(0, prev.totalItems - 1),
        }));

        toast.success(
          `${userInfo.name}'s join request has been ${action}d successfully!`
        );

        // If we emptied the current page and there are previous pages, go back
        if (joinRequests.length === 1 && pagination.currentPage > 1) {
          setPagination((prev) => ({
            ...prev,
            currentPage: prev.currentPage - 1,
          }));
        }
      } else {
        throw new Error(`Failed to ${action} join request`);
      }
    } catch (error) {
toast.error(error.message || `Failed to ${action} join request`);
    } finally {
      setProcessingRequests((prev) => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else if (diffInHours < 168) {
      // Less than a week
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    } else {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: newPage,
    }));
  };

  if (loading) {
    return (
      <div className="join-requests-loading">
        <RefreshCw className="loading-spinner" size={24} />
        <p>Loading join requests...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="join-requests-error">
        <AlertCircle size={48} />
        <h3>Error Loading Join Requests</h3>
        <p>{error}</p>
        <button className="retry-button" onClick={fetchJoinRequests}>
          <RefreshCw size={16} />
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="join-request-management">
      <div className="join-requests-header">
        <div className="header-info">
          <div className="header-icon">
            <Users size={24} />
          </div>
          <div className="header-text">
            <h2>Join Requests</h2>
            <p>Review and manage pending membership requests</p>
          </div>
        </div>
        <div className="header-stats">
          <div className="stat-item">
            <Clock size={16} />
            <span>{pagination.totalItems} pending</span>
          </div>
        </div>
      </div>

      {joinRequests.length === 0 ? (
        <div className="no-join-requests">
          <Mail size={48} />
          <h3>No Pending Requests</h3>
          <p>All caught up! No pending join requests at the moment.</p>
        </div>
      ) : (
        <>
          <div className="join-requests-list">
            {joinRequests.map((request) => (
              <div key={request._id} className="join-request-card">
                <div className="request-user-info">
                  <div className="user-avatar">
                    {request.user.profilePhotoUrl ? (
                      <img
                        src={request.user.profilePhotoUrl}
                        alt={request.user.name}
                        className="avatar-image"
                      />
                    ) : (
                      <User size={20} />
                    )}
                  </div>
                  <div className="user-details">
                    <h4 className="user-name">{request.user.name}</h4>
                    <div className="user-meta">
                      <span className="user-email">{request.user.email}</span>
                      <div className="request-time">
                        <Calendar size={12} />
                        <span>Requested {formatDate(request.requestedAt)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {request.message && (
                  <div className="request-message">
                    <div className="message-header">
                      <MessageSquare size={14} />
                      <span>Message</span>
                    </div>
                    <p className="message-text">{request.message}</p>
                  </div>
                )}

                <div className="request-actions">
                  <button
                    className="action-button approve"
                    onClick={() =>
                      handleJoinRequest(
                        request.user._id,
                        "approve",
                        request.user
                      )
                    }
                    disabled={processingRequests.has(request.user._id)}
                  >
                    {processingRequests.has(request.user._id) ? (
                      <RefreshCw size={16} className="spinner" />
                    ) : (
                      <Check size={16} />
                    )}
                    Approve
                  </button>
                  <button
                    className="action-button reject"
                    onClick={() =>
                      handleJoinRequest(
                        request.user._id,
                        "reject",
                        request.user
                      )
                    }
                    disabled={processingRequests.has(request.user._id)}
                  >
                    {processingRequests.has(request.user._id) ? (
                      <RefreshCw size={16} className="spinner" />
                    ) : (
                      <X size={16} />
                    )}
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>

          {pagination.totalPages > 1 && (
            <div className="join-requests-pagination">
              <button
                className="pagination-button"
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.hasPrev}
              >
                <ChevronLeft size={16} />
                Previous
              </button>

              <div className="pagination-info">
                <span>
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>
                <span className="total-items">
                  ({pagination.totalItems} total requests)
                </span>
              </div>

              <button
                className="pagination-button"
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.hasNext}
              >
                Next
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default JoinRequestManagement;
